import React, { useRef, useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import TextField from "@mui/material/TextField";
import CustomTypography from "../global-styles/fonts";
import { CircularProgress } from "@mui/material";
import fetchProvinces from "../../services/fetch-provinces";
import fetchCountries from "../../services/fetch-countries";

const AddressLocationFields = ({
  address,
  errors,
  onProvinceChange,
  onCountryChange,
  onZipChange,
  onCityChange,
  type = "shipping",
  onProvinceSelect,
  onDataChange,
}) => {
  const selectedCountry = useRef();
  const [isLoadingZip, setIsLoadingZip] = useState(false);
  const [zipError, setZipError] = useState("");
  const places = useRef();
  const debounceTimeout = useRef();

  const fetchLocationByZip = async (countryCode, zipCode) => {
    if (!countryCode || !zipCode || zipCode.trim().length < 3) {
      return;
    }

    setIsLoadingZip(true);
    setZipError("");

    try {
      const response = await fetch(
        `https://api.zippopotam.us/${countryCode}/${zipCode.trim()}`
      );

      if (!response.ok) {
        throw new Error("Invalid zip code or country combination");
      }

      const data = await response.json();

      if (data && data.places && data.places.length > 0) {
        places.current = data.places;
        if (data.places.length > 1) {
          //check if all the places have the same state abbreviation
          const uniqueStates = new Set(
            data.places.map((place) => place["state abbreviation"])
          );
          if (uniqueStates.size === 1) {
            const response = await fetchProvinces(
              15,
              0,
              "",
              "code",
              selectedCountry?.current?._id,
              [data.places[0]["state abbreviation"]]
            );
            if (response && response.total === 1) {
              const province = response.data[0];
              onProvinceChange(province);
            }
          }
        } else {
          onCityChange(data.places[0]["place name"]);
          const response = await fetchProvinces(
            15,
            0,
            "",
            "code",
            selectedCountry?.current?._id,
            [data.places[0]["state abbreviation"]]
          );
          if (response && response.total === 1) {
            const province = response.data[0];
            onProvinceChange(province);
          }
        }
      } else {
        setZipError("No state/province found for this zip code");
      }
    } catch (error) {
      console.error("Error fetching location data:", error);
      setZipError("Invalid zip code or unable to fetch location data");
      onProvinceChange(null);
      onCityChange("");
      places.current = null;
    } finally {
      setIsLoadingZip(false);
    }
  };

  // Handle zip code change with 3-second debounce
  const handleZipChange = (value) => {
    onZipChange(value);
    setZipError("");

    // Clear existing timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Set new timeout for 3 seconds
    debounceTimeout.current = setTimeout(() => {
      if (selectedCountry.current?.code2 && value.trim().length >= 3) {
        fetchLocationByZip(selectedCountry.current.code2.toLowerCase(), value);
      }
    }, 2000); // 2 second delay
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const onChangeCountry = (country) => {
    onZipChange("");
    onCityChange("");
    places.current = null;
    selectedCountry.current = country;
    onCountryChange(country);
    setZipError("");
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
  };

  const onChangeProvince = (province) => {
    setZipError("");
    onCityChange("");
    onZipChange("");
    places.current = null;
    onProvinceSelect(province);
  };

  return (
    <>
      <Grid item xs={12} md={4}>
        <CustomTypography
          style={{ textAlign: "left", fontWeight: 400 }}
          fontSize={14}
        >
          City <span style={{ color: "red" }}>*</span>
        </CustomTypography>
        <AutocompleteWithPagination
          hideLabel
          selectedItem={
            address?.city
              ? { "place name": address?.city }
              : { "place name": "" }
          }
          customLabel={"City"}
          onDataChange={(city) => onCityChange(city ? city["place name"] : "")}
          width="100%"
          optionKey="place name"
          data={places.current?.length ? places.current : []}
          errorMessage={errors[`${type}_city`]}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <CustomTypography
          style={{ textAlign: "left", fontWeight: 400 }}
          fontSize={14}
        >
          State/Province <span style={{ color: "red" }}>*</span>
        </CustomTypography>
        <AutocompleteWithPagination
          hideLabel
          selectedItem={
            address?.province ? { name: address?.province } : { name: "" }
          }
          customLabel={"Province"}
          onDataChange={(province) => onChangeProvince(province)}
          width="100%"
          optionKey="name"
          apiEndPoint={(limit, skip, search = "") =>
            fetchProvinces(
              limit,
              skip,
              search,
              "name",
              selectedCountry?.current?._id
            )
          }
          errorMessage={errors[`${type}_province`]}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <CustomTypography
          style={{ textAlign: "left", fontWeight: 400 }}
          fontSize={14}
        >
          State/Province Code <span style={{ color: "red" }}>*</span>
        </CustomTypography>
        <AutocompleteWithPagination
          hideLabel
          selectedItem={
            address?.province_code
              ? { code: `${address?.province_code} (${address?.province})` }
              : { code: "" }
          }
          customLabel={"Province Code"}
          onDataChange={onChangeProvince}
          width="100%"
          optionKey={(province) =>
            `${province.code} ${province.name ? `(${province.name})` : ""}`
          }
          apiEndPoint={(limit, skip, search = "") =>
            fetchProvinces(
              limit,
              skip,
              search,
              "code",
              selectedCountry?.current?._id,
              places?.current?.map((place) => place["state abbreviation"])
            )
          }
          errorMessage={errors[`${type}_province_code`]}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <CustomTypography
          style={{ textAlign: "left", fontWeight: 400 }}
          fontSize={14}
        >
          Country <span style={{ color: "red" }}>*</span>
        </CustomTypography>
        <AutocompleteWithPagination
          hideLabel
          selectedItem={
            address?.country ? { name: address?.country } : { name: "" }
          }
          customLabel={"Country"}
          onDataChange={(country) => onChangeCountry(country)}
          width="100%"
          optionKey="name"
          apiEndPoint={(limit, skip, search = "") =>
            fetchCountries(limit, skip, search, "name")
          }
          errorMessage={errors[`${type}_country`]}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <CustomTypography
          style={{ textAlign: "left", fontWeight: 400 }}
          fontSize={14}
        >
          Country Code <span style={{ color: "red" }}>*</span>
        </CustomTypography>
        <AutocompleteWithPagination
          hideLabel
          selectedItem={
            address?.country_code
              ? { code2: `${address?.country_code} (${address?.country})` }
              : { code2: "" }
          }
          customLabel={"Country Code"}
          onDataChange={(country) => onChangeCountry(country)}
          width="100%"
          optionKey={(country) =>
            `${country.code2} ${country.name ? `(${country.name})` : ""}`
          }
          apiEndPoint={(limit, skip, search = "") =>
            fetchCountries(limit, skip, search, "code2")
          }
          errorMessage={errors[`${type}_country_code`]}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <CustomTypography
          style={{ textAlign: "left", fontWeight: 400, marginBottom: 1 }}
          fontSize={14}
        >
          Zip <span style={{ color: "red" }}>*</span>
          {isLoadingZip && (
            <CircularProgress size={12} sx={{ ml: 1, color: "#1976d2" }} />
          )}
        </CustomTypography>
        <TextField
          size="small"
          required
          fullWidth
          value={address?.zip || ""}
          onChange={(e) => handleZipChange(e.target.value)}
          error={!!errors[`${type}_zip`]}
          helperText={
            errors[`${type}zip`] ||
            zipError ||
            (selectedCountry.current?.code2
              ? "Enter zip code to auto-populate location details (~2 second delay)"
              : "Select country first")
          }
          disabled={!selectedCountry.current?.code2}
          placeholder={
            selectedCountry.current?.code2
              ? "Enter zip code..."
              : "Select country first"
          }
        />
      </Grid>
    </>
  );
};

export default AddressLocationFields;
